<template>
    <div class="mb-3">
        <div class="mb-2 flex items-center">
            <button
                type="button"
                class="group flex items-center gap-1.5 text-lg font-medium text-gray-400 transition-colors hover:text-gray-300"
                @click="expanded = !expanded"
            >
                Reviews
                <ChevronDownIcon
                    class="h-4 w-4 transition-transform duration-200"
                    :class="expanded ? 'rotate-180' : ''"
                />
            </button>
            <button
                v-if="hasPermission('review.cashouts')"
                @click.prevent="open = true"
                :disabled="loading"
                type="button"
                :class="{ 'cursor-not-allowed': loading }"
                class="btn-primary-xs ml-auto"
            >
                Avaliar
            </button>
        </div>

        <review-modal
            :cashout="cashout"
            :open="open"
            @close="open = false"
            @fetch="
                open = false;
                fetch();
            "
        />

        <card
            class="overflow-hidden transition-colors"
            :class="!expanded && !loading ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40' : ''"
            :size="expanded ? 'md' : 'sm'"
            @click="handleCollapsedClick"
        >
            <div
                v-if="!expanded && !loading"
                class="flex w-full items-center gap-2"
            >
                <div class="flex min-w-0 flex-1 items-center justify-start">
                    <div
                        v-for="(user, index) in users"
                        :key="user.id"
                        v-tooltip="avatarTooltip(user)"
                        class="relative shrink-0"
                        :style="{ zIndex: users.length - index }"
                        :class="index > 0 ? '-ml-2' : ''"
                    >
                        <dice-bear-avatar
                            :seed="user.name"
                            :url="user.avatar"
                            class="h-8 w-8 rounded-full ring-2 ring-white dark:ring-gray-800"
                        />
                        <span
                            v-if="isApproved(user)"
                            class="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-emerald-500 ring-2 ring-white dark:ring-gray-800"
                        >
                            <CheckIcon class="h-2 w-2 text-white" aria-hidden="true" />
                        </span>
                    </div>
                </div>
                <span
                    v-if="approvedCount > 0"
                    class="shrink-0 text-xs font-medium text-emerald-600 dark:text-emerald-400"
                >
                    {{ approvedCount }}/{{ users.length }}
                </span>
            </div>

            <div
                v-if="!expanded && loading"
                class="flex items-center justify-start"
            >
                <div
                    v-for="n in 4"
                    :key="n"
                    class="h-8 w-8 shrink-0 animate-pulse rounded-full bg-gray-500"
                    :class="n > 1 ? '-ml-2' : ''"
                ></div>
            </div>

            <div
                v-show="expanded"
                class="border-t border-gray-100 dark:border-gray-700/50"
            >
                <div v-if="loading" class="px-4 py-3">
                    <div
                        v-for="n in 3"
                        :key="n"
                        class="animate-pulse flex space-x-4 flex-col"
                        :class="{ 'mb-3': n !== 3 }"
                    >
                        <div class="flex space-y-6 py-1 items-center">
                            <div class="h-8 w-8 rounded-full -mr-3 bg-gray-500"></div>
                            <div class="flex-grow mt-0 ml-5" style="margin-top: 0 !important;">
                                <div class="h-2 bg-gray-500 rounded w-full mb-3"></div>
                                <div class="h-2 bg-gray-500 rounded w-3/4"></div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else class="flex flex-col px-1">
                    <cashout-review
                        v-for="user in users"
                        :key="user.id"
                        :user="user"
                        :cashout="cashout"
                        @fetch="fetch"
                    />
                </div>
            </div>
        </card>
    </div>
</template>

<script>
import Card from "../../layouts/Card.vue";
import CashoutReview from "./CashoutReview.vue";
import permissionMixin from "../../../mixins/PermissionMixin.vue";
import ReviewModal from "./ReviewModal.vue";
import DiceBearAvatar from "../../common/DiceBearAvatar.vue";
import { CheckIcon } from "@heroicons/vue/20/solid";
import { ChevronDownIcon } from "@heroicons/vue/24/outline";

export default {
    components: {
        ReviewModal,
        CashoutReview,
        Card,
        DiceBearAvatar,
        CheckIcon,
        ChevronDownIcon,
    },
    props: ["cashout"],
    mixins: [permissionMixin],

    data() {
        return {
            loading: true,
            users: [],
            open: false,
            expanded: false,
        };
    },

    computed: {
        approvedCount() {
            return this.users.filter((user) => this.isApproved(user)).length;
        },
    },

    watch: {
        cashout() {
            if (this.cashout) {
                this.fetch();
            }
        },
    },

    mounted() {
        if (this.cashout) {
            this.fetch();
        }
    },

    methods: {
        handleCollapsedClick() {
            if (!this.expanded && !this.loading) {
                this.expanded = true;
            }
        },
        fetch() {
            this.loading = true;
            axios
                .get("/api/cashout/" + this.cashout.id + "/review")
                .then(({ data }) => {
                    this.users = data;
                    this.loading = false;
                })
                .catch(() => {
                    this.loading = false;
                });
        },
        isApproved(user) {
            return user.cashoutReview?.status === "approved";
        },
        avatarTooltip(user) {
            const name = user.name || "Usuário";

            if (!user.cashoutReview) {
                return `${name} — Sem review`;
            }

            return `${name} — ${this.statusName(user.cashoutReview.status)}`;
        },
        statusName(name) {
            switch (name) {
                case "pending":
                    return "Solicitado";
                case "approved":
                    return "Aprovado";
                case "warning":
                    return "Atenção";
                case "rejected":
                    return "Reprovado";
                default:
                    return name;
            }
        },
    },
};
</script>
